import { appOverride, appVariant } from '@util/AppUtils';
import { Environment } from '@util/Environment';
import { choicesToLabels } from '@util/FlowUtils';
import { NervaUserData } from 'hydra-data-types';
import moment from 'moment';
import React, { ReactElement } from 'react';
import { ArrayUtils } from 'ts-utils';
import { LayoutScreenConfig, ScreenType } from '../../config/FlowTypes';
import { AppSelector, useAppStore } from '../../store/Store';
import { PaddedAccentContainer } from '../styles/ColoredContainers';
import { ScreenWrapper } from '../styles/Containers';
import { BridgeType, StepList, StepListProps, StepListTitle } from '../styles/StepList';
import { BodySM } from '../styles/Typography';
import { NavigationProps } from './FlowNavigation';
import { PresentationLayout } from './layouts/PresentationLayout';

type ProgramExperienceLayoutConfig = {
  title?: string;
  topBody: string[];
  showDate?: boolean;
  bottomBody?: string[];
  listTitle?: string;
  listItems?: StepListProps['items'];
  primaryButtonTitle: string;
  textAnimationEnabled?: boolean;
};

export type ProgramExperienceScreenConfig<ScreenNames extends string> = LayoutScreenConfig<
  ScreenType.ProgramExperience,
  ProgramExperienceLayoutConfig,
  ScreenNames
>;

type Props<ScreenNames extends string> = NavigationProps<
  ProgramExperienceScreenConfig<ScreenNames>,
  ScreenNames
>;

export const ProgramExperienceScreen = <ScreenNames extends string>(
  props: Props<ScreenNames>
): ReactElement => {
  const { layout, navigation } = props;
  const userData = useAppStore(AppSelector.userData);

  // We use the same algorithm to calculate the date in Pain as Nerva. If this changes, we will add painResultsDate
  const date = appVariant({
    nerva: resultsDate(),
    meno: undefined,
    quit: quitDate(),
    pain: resultsDate(),
  });

  if (layout.showDate && date === undefined) {
    throw new Error(`No date values exists for ${Environment.APP}`);
  }

  const bottomBodyCopy =
    layout.bottomBody?.map((content) => ({ type: 'text' as const, content })) ?? [];

  function parseGoals() {
    const goals = appOverride({
      default: [],
      nerva: choicesToLabels((userData as NervaUserData).freeFromWorry),
    });
    // Filter out "More/other" from the goals list
    const filteredGoals = goals.filter((goal) => goal !== 'More/other');
    return ArrayUtils.toHumanReadableString(filteredGoals);
  }

  const listItemsOrGoals = layout.listItems
    ? {
        type: 'custom' as const,
        content: (
          <>
            <StepListTitle bold>{layout.listTitle}</StepListTitle>
            <StepList bridgeType={BridgeType.Plus} items={layout.listItems} />
          </>
        ),
      }
    : { type: 'text' as const, content: `${parseGoals()}${parseGoals() !== '' ? '.' : ''}` };

  return (
    <ScreenWrapper>
      <PresentationLayout
        title={layout.title}
        content={[
          ...layout.topBody.map((content) => ({
            type: 'text' as const,
            content,
          })),
          {
            type: 'custom' as const,
            content: (
              <>
                {layout.showDate && (
                  <PaddedAccentContainer center borderRadius style={{ padding: 14 }}>
                    {/* BodySM has only one props - bold (boolean) which applies font-weight:medium
                     *   helvetica font found on my mac only has 3 valid font-wieghts: light; normal; and bold,
                     *   and testing showed that fontWeight='medium' is equivalent to 'normal' (400).
                     *   We pass prop style.fontWeight='bold' here to get a 600 font weight.
                     *   We pass prop style.margin=0 to remove antd's bottom margin of 18px.
                     */}
                    <BodySM
                      style={{
                        textAlign: 'center',
                        fontWeight: 'bold',
                        margin: 0,
                      }}
                    >
                      {date}
                    </BodySM>
                  </PaddedAccentContainer>
                )}
              </>
            ),
          },
          ...bottomBodyCopy,
          listItemsOrGoals,
        ]}
        onPressPrimaryButton={() => navigation.next(props)}
        primaryButtonTitle={layout.primaryButtonTitle}
        textAnimationEnabled={layout.textAnimationEnabled}
      />
    </ScreenWrapper>
  );
};

const DATE_STRING_FORMAT = 'dddd, MMMM Do YYYY'; // e.g. Saturday, October 29th 2022

function quitDate(): string {
  /**
   * From Quit v4 (when the prescriptive program structure was introduced), the Stop Date defaults to the
   * 9th day from when inboarding is completed.
   */
  const NUM_DAYS_TO_QUIT_SMOKING = 9;
  return moment().add(NUM_DAYS_TO_QUIT_SMOKING, 'days').format(DATE_STRING_FORMAT);
}

export function resultsDate(): string {
  const NUM_WEEKS_TO_RESULTS_DAY = 6;
  return moment().add(NUM_WEEKS_TO_RESULTS_DAY, 'weeks').format(DATE_STRING_FORMAT);
}
